import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { ReceivableSummary } from "src/app/models/receivable-summary.model";
import { Receivable } from "src/app/models/receivable.model";
import { Lot } from "src/app/models/lot.model";
import moment from "moment";
import { Subscription } from "rxjs";

@Component({
    selector: "receivables-report",
    templateUrl: "./receivables-report.component.html",
    styleUrls: ["./receivables-report.component.scss"],
    standalone: false
})
export class ReceivablesReportComponent implements OnInit, OnDestroy {

    /** define if is loading */
    protected loading: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** */
    protected lots: Lot[] = []

    // The search form group
    protected form: FormGroup | null = null

    /** A list of summaries grouped by lot. */
    protected summary: ReceivableSummary | null = null

    /** A list of receivables for the selected lot and/or corporation. */
    protected receivables: Receivable[] | null = null

    /** Pre-computed receivables groups to quickly respond to interactivity changes.  */
    protected grouped: Object = {}

    // A debounce timer for search events. Used to wait for
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for changes to the lot filter. */
    private subscriptions: Subscription[] = []

    /** If the report is filtered by lot this will have a positive value. */
    protected get lotId () {
        return parseInt(this.form.get('lotId').value)
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            group: new FormControl<string>('method', [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.reset()
                this.fetch()
            })
        )

        this.reset()
        this.fetch()

        if (this.store && this.store.userAccount && this.store.userAccount['user'] &&
            this.store.userAccount['user'].lots) {
            this.lots = this.store.userAccount['user'].lots
        }
    }

    ngOnDestroy() {
        this.reset()

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.summary = null
        this.receivables = []
        this.grouped = {}

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Get the summary and list of receivables needed for the report.
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'receivables', 'view')) {
            return
        }

        if (this.loading) { // already getting the data
            return
        }

        let lotParam = ''
        let id = parseInt(this.form.get('lotId').value)
        if (id && (!isNaN(id) && (id > 0))) {
            lotParam = `?lot=${id}`
        }

        this.loading = true
        await this.master.getAsync(`reports/receivables${lotParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.summary = res.data.summary
            this.receivables = res.data.receivables
            this.constructGroups()
        })
    }

    /**
     * Group the receivables for later display based on user selections.
     */
    protected constructGroups = () => {
        if (!this.receivables || (this.receivables.length <= 0)) {
            return
        }

        for (const receivable of this.receivables) {
            const key: string = `${receivable.corporateId}-${receivable.dealershipId}-${receivable.lotId}`
            if (!this.grouped.hasOwnProperty(key)) {
                this.grouped[key] = {
                    name: receivable.lot,
                    summary: new ReceivableSummary(),
                    count: 0,
                    method: {
                        sie: {
                            display: 'Scheduled Installment Earnings',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        tde: {
                            display: 'True Daily Earnings',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        }
                    },
                    status: {
                        sold: {
                            display: 'Sold (acitve)',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        flag: {
                            display: 'Flagged (alert)',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        }
                    },
                    riding: {
                        positive: {
                            display: 'Positive Riding Cash',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        },
                        negative: {
                            display: 'Negative Riding Cash',
                            summary: new ReceivableSummary(),
                            receivables: new Array<Receivable>,
                            count: 0
                        }
                    },
                    collector: {}
                }
            }

            this.grouped[key].count++
            if (!this.grouped[key].summary.asOf || moment(receivable.day).isAfter(this.grouped[key].summary.asOf)) {
                this.grouped[key].summary.asOf = receivable.day
            }

            this.grouped[key].summary.paymentBalance += receivable.paymentBalance
            this.grouped[key].summary.balance += receivable.balance
            this.grouped[key].summary.taxBalance += receivable.taxBalance
            this.grouped[key].summary.payoff += receivable.payoff
            this.grouped[key].summary.unearnedInterest += receivable.unearnedInterest
            this.grouped[key].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
            this.grouped[key].summary.lateFees += receivable.lateFees
            this.grouped[key].summary.ridingCash += receivable.ridingCash

            switch (receivable.method.toLocaleLowerCase()) {
                case 'true daily earnings':
                    this.grouped[key]['method']['tde'].receivables.push(receivable)
                    this.grouped[key]['method']['tde'].count++
                    this.grouped[key]['method']['tde'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['method']['tde'].summary.balance += receivable.balance
                    this.grouped[key]['method']['tde'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['method']['tde'].summary.payoff += receivable.payoff
                    this.grouped[key]['method']['tde'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['method']['tde'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['method']['tde'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['method']['tde'].summary.ridingCash += receivable.ridingCash
                    break
                case 'scheduled installment earnings':
                    this.grouped[key]['method']['sie'].receivables.push(receivable)
                    this.grouped[key]['method']['sie'].count++
                    this.grouped[key]['method']['sie'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['method']['sie'].summary.balance += receivable.balance
                    this.grouped[key]['method']['sie'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['method']['sie'].summary.payoff += receivable.payoff
                    this.grouped[key]['method']['sie'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['method']['sie'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['method']['sie'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['method']['sie'].summary.ridingCash += receivable.ridingCash
                    break
            }

            switch (receivable.status.toLocaleLowerCase()) {
                case 'sold':
                    this.grouped[key]['status']['sold'].receivables.push(receivable)
                    this.grouped[key]['status']['sold'].count++
                    this.grouped[key]['status']['sold'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['status']['sold'].summary.balance += receivable.balance
                    this.grouped[key]['status']['sold'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['status']['sold'].summary.payoff += receivable.payoff
                    this.grouped[key]['status']['sold'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['status']['sold'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['status']['sold'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['status']['sold'].summary.ridingCash += receivable.ridingCash
                    break
                case 'flag':
                    this.grouped[key]['status']['flag'].receivables.push(receivable)
                    this.grouped[key]['status']['flag'].count++
                    this.grouped[key]['status']['flag'].summary.paymentBalance += receivable.paymentBalance
                    this.grouped[key]['status']['flag'].summary.balance += receivable.balance
                    this.grouped[key]['status']['flag'].summary.taxBalance += receivable.taxBalance
                    this.grouped[key]['status']['flag'].summary.payoff += receivable.payoff
                    this.grouped[key]['status']['flag'].summary.unearnedInterest += receivable.unearnedInterest
                    this.grouped[key]['status']['flag'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                    this.grouped[key]['status']['flag'].summary.lateFees += receivable.lateFees
                    this.grouped[key]['status']['flag'].summary.ridingCash += receivable.ridingCash
                    break
            }

            if (receivable.ridingCash <= 0) {
                this.grouped[key]['riding']['positive'].receivables.push(receivable)
                this.grouped[key]['riding']['positive'].count++
                this.grouped[key]['riding']['positive'].summary.paymentBalance += receivable.paymentBalance
                this.grouped[key]['riding']['positive'].summary.balance += receivable.balance
                this.grouped[key]['riding']['positive'].summary.taxBalance += receivable.taxBalance
                this.grouped[key]['riding']['positive'].summary.payoff += receivable.payoff
                this.grouped[key]['riding']['positive'].summary.unearnedInterest += receivable.unearnedInterest
                this.grouped[key]['riding']['positive'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                this.grouped[key]['riding']['positive'].summary.lateFees += receivable.lateFees
                this.grouped[key]['riding']['positive'].summary.ridingCash += receivable.ridingCash
            }
            else {
                this.grouped[key]['riding']['negative'].receivables.push(receivable)
                this.grouped[key]['riding']['negative'].count++
                this.grouped[key]['riding']['negative'].summary.paymentBalance += receivable.paymentBalance
                this.grouped[key]['riding']['negative'].summary.balance += receivable.balance
                this.grouped[key]['riding']['negative'].summary.taxBalance += receivable.taxBalance
                this.grouped[key]['riding']['negative'].summary.payoff += receivable.payoff
                this.grouped[key]['riding']['negative'].summary.unearnedInterest += receivable.unearnedInterest
                this.grouped[key]['riding']['negative'].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
                this.grouped[key]['riding']['negative'].summary.lateFees += receivable.lateFees
                this.grouped[key]['riding']['negative'].summary.ridingCash += receivable.ridingCash
            }

            if (!receivable.collector || (receivable.collectorId <= 0)) {
                receivable.collector = 'Unassigned'
            }

            if (!this.grouped[key]['collector'].hasOwnProperty(receivable.collector)) {
                this.grouped[key]['collector'][receivable.collector] = {
                    display: receivable.collector,
                    summary: new ReceivableSummary(),
                    receivables: new Array<Receivable>,
                    count: 0
                }
            }

            this.grouped[key]['collector'][receivable.collector].receivables.push(receivable)
            this.grouped[key]['collector'][receivable.collector].count++
            this.grouped[key]['collector'][receivable.collector].summary.paymentBalance += receivable.paymentBalance
            this.grouped[key]['collector'][receivable.collector].summary.balance += receivable.balance
            this.grouped[key]['collector'][receivable.collector].summary.taxBalance += receivable.taxBalance
            this.grouped[key]['collector'][receivable.collector].summary.payoff += receivable.payoff
            this.grouped[key]['collector'][receivable.collector].summary.unearnedInterest += receivable.unearnedInterest
            this.grouped[key]['collector'][receivable.collector].summary.unpaidEarnedInterest += receivable.unpaidEarnedInterest
            this.grouped[key]['collector'][receivable.collector].summary.lateFees += receivable.lateFees
            this.grouped[key]['collector'][receivable.collector].summary.ridingCash += receivable.ridingCash
        }
    }

    /**
     * listen to output of navbar
    */
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.lots = this.store.userAccount['user'].lots
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
